import { ThirdPartyCost } from '@app/core/model/third-party-cost.model'
import { CompanyScopeCustomFieldsDefinition } from '@app/features/scope-overview/model/company-scope-custom-fields-definition.model'
import { FeeAmountType, FeeItemInstance } from '@app/features/scope-overview/model/fee-item.model'
import { Money } from '@app/features/scope-overview/model/money.model'
import { ScopeCustomFieldValueStructure } from '@app/features/scope-overview/model/scope-custom-field-value-structure.model'
import { ScopeIdentity } from '@app/features/scope-overview/model/scope-identity'
import { ScopeReviewer } from '@app/features/scope-overview/model/scope-reviewer.model'
import { ScopeSection } from '@app/features/scope-overview/model/scope-section'
import { Deliverable } from '@app/features/scoping/models/deliverable.model'
import { UserComment } from '@core/model/comment.model'
import { PrivilegeAware } from '@core/model/definitions/privileged-aware.interface'
import { Privilege } from '@core/model/enums/privilege.enum'
import { FolderVersion } from '@core/model/folder-version.model'
import { ScopeSubmitter } from '@core/model/scope-submitter.model'
import { User } from '@core/model/user.model'
import { isNonTradedDeliverable } from '@shared/utils/utils'
import { Type } from 'class-transformer'
import { isNil } from 'lodash'
import { Discount } from './discount.model'
import { ScopeMsaLineItem } from './scope-msa-line-item.model'
import { getTotalFees } from '@app/shared/components/scope-elements/utils'

class ScopeVersionCalculatedValues {
  margin: number
  budgetBalance: number
  deliverableBudgetTotal: number
  markup: number
  rateCardTotalMinutes: number
  totalActualHours: number
  totalActualMinutes: number
  totalPercentageComplete: number
  totalProfit: number
  totalRateCardHours: number
  totalRatecardSellingPrice: number
  totalScopeMarkHours: number
  totalScopeMarkSellingPrice: number
  totalSellingPrice: number
  naturalSellingPrice: number
  @Type(() => Date) updatedOn: Date
}

export class ScopeVersion implements PrivilegeAware {
  id?: number
  name?: string
  status?: StatusType
  scopeApprovedWebHookInfo?: ScopeApprovedWebHookInfo
  approved?: boolean
  archived?: boolean
  finalised: boolean
  collaborators?: ScopeSubmitter[]
  reviewers?: ScopeReviewer[]
  approvers?: any[]
  userPrivilegeRestrictions?: any[]
  userAdditionalPrivileges?: any[]
  version?: number
  margin?: number
  currency?: string
  currencyUnit?: string
  description?: string
  internalNote?: string
  isEditable?: boolean
  isTrafficked?: any
  isDeliverablesUninitialised?: boolean
  calculatedValues?: ScopeVersionCalculatedValues
  traffickable?: boolean
  traffickableThroughWorkato?: boolean
  trafficSystemEntityMetadata?: any
  defaultView?: DefaultView = DefaultView.table
  dynamicFields?: Record<string, any>
  naturalSellingPrice?: Money
  configuration: ScopeConfiguration
  configurationSubmitted?: boolean
  componentModifierValues?: Record<number, number>
  selected?: boolean
  thirdPartyCostBudget?: Money
  retainedHoursUsed?: number
  scopeRoles?: any[]
  readyToTraffic: boolean
  containsUnmappedScopeMarkRoles?: boolean
  useRetainedHours?: boolean
  totalSellingPrice?: Money
  budgetBalance?: Money
  deliverableBudgetTotal?: Money
  @Type(() => Date) startDate?: Date
  @Type(() => Date) endDate?: Date
  @Type(() => User) createdBy?: User
  @Type(() => Date) createdTs?: Date
  @Type(() => Money) budget?: Money
  @Type(() => ScopeIdentity) identity?: ScopeIdentity
  @Type(() => FolderVersion) scopeOfWorkVersion?: FolderVersion
  @Type(() => ScopeCustomFieldValueStructure) scopeVersionCustomFieldValueStructure?: ScopeCustomFieldValueStructure
  @Type(() => Deliverable) deliverables?: Deliverable[]
  @Type(() => ScopeSection) sections?: ScopeSection[]
  @Type(() => ThirdPartyCost) thirdPartyCosts: ThirdPartyCost[]
  @Type(() => Money) tpcValueTotal?: Money
  @Type(() => Discount) discount?: Discount
  @Type(() => FeeItemInstance) feeItems?: FeeItemInstance[]
  @Type(() => UserComment) comments?: UserComment[]
  @Type(() => ScopeMsaLineItem) msaLineItem: ScopeMsaLineItem
  @Type(() => Date) lastTraffickedDate?: Date
  @Type(() => CompanyScopeCustomFieldsDefinition)
  companyScopeCustomFieldsDefinition?: CompanyScopeCustomFieldsDefinition

  isScopeByRole = () => this.identity?.identificationType === 'SCOPE_BY_ROLE'
  isScopeByScenario = () => this.identity?.identificationType === 'SCOPE_BY_SCENARIO'

  getRateCardCurrency = (): string => this.identity?.getRateCard().currencyCode

  private sumMoney = (total: Money, current: Money): Money =>
    new Money(isNil(total) ? current?.amount : total.amount + current.amount, this.currency)

  getStatusText(): string {
    const statusText = this.status ? this.status.toString().replace(/_/, ' ') : ''
    return statusText.charAt(0).toUpperCase() + statusText.toLowerCase().slice(1)
  }

  getAllocatedBudget(): Money {
    return this.deliverables
      .filter(isNonTradedDeliverable)
      .map((d) => d.budget)
      .filter((budget): budget is Money => !isNil(budget))
      .reduce(this.sumMoney, new Money(0, this.currency))
  }

  getUnallocatedBudget(): Money {
    const totalDeliverableBudget = this.getTotalDeliverableBudget()
    if (!this.budget || !totalDeliverableBudget) return new Money(0, this.currency)
    return this.budget.subtractValue(totalDeliverableBudget.amount || 0)
  }

  getSectionDeliverables(scopeSection: ScopeSection) {
    return this.deliverables?.filter((d) => d.section?.id === scopeSection.id)
  }

  getTotalSellingPrice(ctx = { excludeFees: false, excludeMsa: false, excludeDiscount: false }): Money {
    if (this.containsUnmappedScopeMarkRoles) return null

    if (this.calculatedValues && !ctx.excludeFees && !ctx.excludeMsa && !ctx.excludeDiscount) {
      return new Money(this.calculatedValues.totalSellingPrice, this.currency)
    }

    if (this.isDeliverablesUninitialised) return this.totalSellingPrice

    const total = this.getNaturalSellingPrice({ includeTpc: true })
    let currentTotal = total?.amount

    if (this.feeItems != null && !ctx.excludeFees) {
      for (const feeItem of this.feeItems) {
        if (feeItem.feeItem) {
          let val = feeItem.feeItem?.amount
          if (feeItem.feeItem.amountType === FeeAmountType.PERCENTAGE) val = total.amount * (val / 100)
          currentTotal = currentTotal + val
        }
      }
    }

    // Check for discount on scope
    if (this.discount != null && !ctx.excludeDiscount) {
      const discountValue = parseFloat(this.discount.getValue().toString())
      if (this.discount.getType() == 'AMOUNT') {
        currentTotal = currentTotal - discountValue
      } else {
        currentTotal = currentTotal - currentTotal * (discountValue / 100)
      }
    }

    // Check for MSA line item.
    if (this.msaLineItem != null && !ctx.excludeMsa) {
      let val: number = parseFloat(this.msaLineItem.value.toString())
      if (this.msaLineItem?.type == 'PERCENTAGE') {
        val = currentTotal * (val / 100)
      }

      if (this.msaLineItem.chargeType == 'FEE') {
        currentTotal = currentTotal + val
      } else {
        currentTotal = currentTotal - val
      }
    }

    return new Money(currentTotal, total?.currency)
  }

  getNaturalSellingPrice(ctx = { includeTpc: false }): Money {
    if (this.calculatedValues) return new Money(this.calculatedValues.naturalSellingPrice, this.currency)
    if (this.isDeliverablesUninitialised) return this.naturalSellingPrice

    let total = new Money(0, this.currency)

    this.deliverables
      ?.filter((d) => !d.section && isNonTradedDeliverable(d))
      ?.forEach((d) => (total = total.add(d.getTotalSellingPrice())))

    if (ctx.includeTpc && this.thirdPartyCosts?.length !== 0) {
      total = total.add(this.getScopeThirdPartyCostSum())
    }

    this.sections?.forEach((s) => {
      const totalSellingPrice = this.getMoneyWithScopeCurrency(s.getTotalSellingPrice(this.deliverables)?.amount || 0)
      total = total.add(totalSellingPrice)
    })

    return total
  }

  getTotalDeliverableBudget(): Money {
    if (this.calculatedValues) return new Money(this.calculatedValues.deliverableBudgetTotal, this.currency)

    if (!this.isDeliverablesUninitialised) {
      return this.deliverables
        .filter(isNonTradedDeliverable)
        .map((d) => d.budget)
        .filter((budget): budget is Money => !isNil(budget))
        .reduce(this.sumMoney, new Money(0, this.currency))
    } else {
      return this.deliverableBudgetTotal
    }
  }

  getBudgetBalance(): Money {
    if (this.calculatedValues) return new Money(this.calculatedValues.budgetBalance, this.currency)

    if (!this.isDeliverablesUninitialised || this.budgetBalance == null) {
      const totalDeliverableBudget = this.getTotalDeliverableBudget()
      const useBudget =
        (this.budget?.amount || 0) > (totalDeliverableBudget.amount || 0) ? this.budget : totalDeliverableBudget
      const scopeValueMoney = this.getTotalSellingPrice()
      return useBudget.subtractValue(scopeValueMoney ? scopeValueMoney.amount || 0 : 0)
    } else {
      return this.budgetBalance
    }
  }

  getMarkup(): number {
    if (this.calculatedValues) return this.calculatedValues.markup

    let totalValue = 0
    let totalCost = 0
    this.deliverables.forEach(function (deliverable) {
      if (deliverable.getTotalSellingPrice() != null && deliverable.getTotalSellingPrice() != undefined) {
        totalValue += parseFloat(String(deliverable.getTotalSellingPrice().amount))
      }
      if (deliverable.getTotalCostPrice() != null && deliverable.getTotalCostPrice() != undefined) {
        totalCost += parseFloat(String(deliverable.getTotalCostPrice().amount))
      }
    })
    const scopeFees = this.getTotalFees()
    const sectionFees = this.sections?.reduce((sum, s) => sum + (s.getTotalFees() || 0), 0) || 0

    totalValue = totalValue + scopeFees + sectionFees
    if (totalCost == 0) {
      return 100.0
    }
    const grossProfit = totalValue - totalCost
    if (grossProfit == 0) {
      return 100.0
    }
    return parseFloat(((grossProfit / totalCost) * 100).toFixed(2))
  }

  getTotalProfit(targetDeliverables?: Deliverable[]): Money {
    if (this.calculatedValues) return new Money(this.calculatedValues.totalProfit, this.currency)

    const isScopeTotalling = targetDeliverables == null
    const deliverables = targetDeliverables ? targetDeliverables : this.deliverables

    let result = new Money(0, this.currency)
    deliverables.forEach((deliverable) => {
      result = result.add(deliverable.getTotalProfit())
    })

    if (this.thirdPartyCosts?.length) this.thirdPartyCosts.forEach((cost) => (result = result.add(cost.getProfit())))

    if (isScopeTotalling) {
      if (this.feeItems?.length > 0) {
        result = result.addValue(this.getTotalFees())
      }
      if (this.sections?.length > 0) {
        this.sections.forEach((section) => {
          const sectionFees = section.getTotalFees()
          if (sectionFees != null) {
            result = result.addValue(sectionFees)
          }
        })
      }
      const totalDiscount = this.totalDiscount()
      if (totalDiscount) {
        result = result.subtractValue(totalDiscount.amount)
      }
      if (this.msaLineItem != null) {
        let val: number = parseFloat(this.msaLineItem.value.toString())
        const currentTotal = this.getTotalSellingPrice({
          excludeFees: false,
          excludeDiscount: false,
          excludeMsa: true,
        })

        if (this.msaLineItem?.type == 'PERCENTAGE') {
          val = currentTotal.amount * (val / 100)
        }
        if (this.msaLineItem?.chargeType == 'FEE') {
          result = result.addValue(val)
        } else {
          result = result.subtractValue(val)
        }
      }
    }

    return result
  }

  getCachedTotalScopeMarkHours() {
    if (this.calculatedValues) return this.calculatedValues.totalScopeMarkHours

    return this.deliverables.reduce((total, deliverable) => total + deliverable.scopeMarkTotalHours, 0)
  }

  getScopeThirdPartyCostSum() {
    let currentTotal = 0
    this.thirdPartyCosts?.forEach((cost) => {
      currentTotal = currentTotal + cost.calculateSellingPrice().amount
    })
    return new Money(currentTotal, this.getRateCardCurrency())
  }

  totalScopeMarkSellingPrice(excludeDeliverableId?: number) {
    const currency = this.getRateCardCurrency()

    if (this.calculatedValues) return new Money(this.calculatedValues.totalScopeMarkSellingPrice, currency)

    let total = new Money(null, currency)
    for (let i = 0; i < this.deliverables.length; i++) {
      const deliverable = this.deliverables[i]
      if (excludeDeliverableId && excludeDeliverableId == deliverable.id) {
        continue
      }
      if (deliverable.totalScopeMarkSellingPrice(currency) == null) return null
      total = total.add(deliverable.totalScopeMarkSellingPrice(currency))
    }

    return total
  }

  totalDiscount() {
    if (isNil(this.identity?.getRateCard())) null

    if (this.discount != null) {
      return new Money(
        this.discount.getValueByPercentage(
          this.getNaturalSellingPrice({ includeTpc: true })
            ? this.getNaturalSellingPrice({ includeTpc: true }).amount + this.getTotalFees()
            : null
        ),
        this.getRateCardCurrency()
      )
    } else {
      return new Money(0, this.getRateCardCurrency())
    }
  }

  getTotalMargin(targetDeliverables: Deliverable[] = []): number {
    const isScopeTotalling = targetDeliverables == null || targetDeliverables.length === 0
    const deliverables = targetDeliverables && targetDeliverables.length > 0 ? targetDeliverables : this.deliverables

    if (isScopeTotalling) {
      const totalSellingPrice = this.getTotalSellingPrice()
      const totalProfit = this.getTotalProfit(null)

      if (totalSellingPrice == null || totalSellingPrice.amount == 0) {
        return 0
      }

      return parseFloat((((totalProfit.amount || 0) / totalSellingPrice.amount) * 100).toFixed(2))
    }

    let totalValue = 0
    deliverables.forEach(function (deliverable) {
      if (deliverable.getTotalSellingPrice() != null && deliverable.getTotalSellingPrice() != undefined) {
        totalValue += deliverable.getTotalSellingPrice().amount
      }
    })

    const totalProfit = this.getTotalProfit(deliverables)

    if (totalValue == 0) {
      return 0
    }

    return parseFloat((((totalProfit.amount || 0) / totalValue) * 100).toFixed(2))
  }

  getTotalMarkup(targetDeliverables: Deliverable[] = []): number {
    const deliverables = targetDeliverables ? targetDeliverables : this.deliverables

    let totalCost = 0
    let totalProfit = 0

    deliverables.forEach(function (deliverable) {
      if (deliverable.getTotalCostPrice() != null && deliverable.getTotalCostPrice() != undefined) {
        totalCost += deliverable.getTotalCostPrice()?.amount
      }
      if (deliverable.getTotalProfit() != null && deliverable.getTotalProfit() != undefined) {
        totalProfit += deliverable.getTotalProfit()?.amount
      }
    })

    if (totalCost == 0) {
      return 0
    }

    const markup = (totalProfit / totalCost) * 100
    return Math.round(markup * 100) / 100
  }

  getTotalRateCardHours(): number {
    if (this.calculatedValues)
      return !isNil(this.calculatedValues.rateCardTotalMinutes)
        ? this.calculatedValues.rateCardTotalMinutes / 60
        : this.calculatedValues.totalRateCardHours

    let total = 0

    this.deliverables.filter(isNonTradedDeliverable).forEach((d) => (total += d.getTotalRateCardHours()))

    return total
  }

  getTotalFees = () => getTotalFees(this)

  getFeeValueTotal() {
    let total = this.getTotalFees()
    const currency = this.getRateCardCurrency()
    this.sections.forEach((section) => {
      const sectionTotal = section.getTotalFees()
      if (sectionTotal != null) {
        total += sectionTotal
      }
    })
    this.deliverables?.forEach((deliverable) => {
      const deliverableTotal = deliverable.getTotalFees()
      if (deliverableTotal != null) {
        total += deliverableTotal
      }
    })
    return new Money(total, currency)
  }

  getAvgMarginOfSection(sectionId) {
    if (sectionId == null || isNaN(sectionId)) {
      throw 'Section ID is not provided'
    }
    const targetDeliverables = this.deliverables.filter((deliverable) => {
      return deliverable.section != null && deliverable.section.id == sectionId
    })
    return this.getTotalMargin(targetDeliverables)
  }

  getAvgMarkupOfSection(sectionId) {
    if (sectionId == null || isNaN(sectionId)) {
      throw 'Section ID is not provided'
    }
    const targetDeliverables = this.deliverables.filter((deliverable) => {
      return deliverable.section != null && deliverable.section.id == sectionId
    })
    return this.getTotalMarkup(targetDeliverables)
  }

  isPrivilegeRestricted(privilege: Privilege) {
    return this.userPrivilegeRestrictions?.indexOf(privilege) != -1
  }

  hasPrivilegeValueRestriction(rule: string, value: number, currentUser: User) {
    let percentage = 0
    const amount = this.naturalSellingPrice?.amount ?? 0
    if (amount && amount > 0) {
      percentage = (value / amount) * 100
    }
    const privilege = currentUser.privilegeGroup?.privilegeRules?.find((r) => r.privilege === rule)
    const ruleRestrictions: any[] = privilege?.restrictions
    const clientRest = ruleRestrictions?.find((r) => r.secondParty && r.secondParty.id === this.identity.secondParty.id)
    const generalRest = ruleRestrictions?.find((r) => !r.secondParty)
    if (!clientRest && !generalRest) {
      return true
    } else if (!clientRest && generalRest) {
      return this.transformArrayAndCheckBudgetByPercentage(generalRest.rangeValuesAsString, percentage)
    } else if (clientRest) {
      return this.transformArrayAndCheckBudgetByPercentage(clientRest.rangeValuesAsString, percentage)
    } else {
      return false
    }
  }

  hasPrivilegeValueRestrictionByScopeValue(rule: string, currentUser: User) {
    const amount = this.naturalSellingPrice?.amount ?? 0
    const privilege = currentUser.privilegeGroup?.privilegeRules?.find((r) => r.privilege === rule)
    const ruleRestrictions: any[] = privilege?.restrictions
    const clientRest = ruleRestrictions?.find((r) => r.secondParty && r.secondParty.id === this.identity.secondParty.id)
    const generalRest = ruleRestrictions?.find((r) => !r.secondParty)

    if (!clientRest && !generalRest) {
      return true
    } else if (!clientRest && generalRest) {
      return this.transformArrayAndCheckBudget(generalRest.rangeValues, amount)
    } else if (clientRest) {
      return this.transformArrayAndCheckBudget(clientRest.rangeValues, amount)
    } else {
      return false
    }
  }

  transformArrayAndCheckBudget(array: string[], budget: number): boolean {
    let min = Infinity
    let max = -Infinity
    if (array.length === 0) {
      return true
    }
    array.forEach((item) => {
      const parts = item.split('_').filter((part) => part)
      parts.forEach((part) => {
        let value
        if (part.includes('K')) {
          value = parseInt(part.replace('K', '000'))
        } else if (part.includes('M')) {
          value = parseInt(part.replace('M', '000000'))
        } else {
          value = parseInt(part)
        }
        if (!isNaN(value)) {
          if (value < min) {
            min = value
          }
          if (value > max) {
            max = value
          }
        }
      })
    })
    return budget >= min && budget <= max
  }

  transformArrayAndCheckBudgetByPercentage(rangeValue: string, percentage: number): boolean {
    // eslint-disable-next-line no-useless-assignment
    let min = Infinity
    // eslint-disable-next-line no-useless-assignment
    let max = -Infinity

    const parts = rangeValue.replace('[', '').replace(']', '').replace('%', '').split('-')
    if (parts.length == 2) {
      min = +parts[0]
      max = +parts[1]
    } else {
      return true
    }
    return percentage >= min && percentage <= max
  }

  hasPrivilege(privilege: Privilege, user: User) {
    const hasAdditional = this.hasAdditionalPrivilege(privilege)
    if (hasAdditional) {
      return true
    }

    const hasPrivilege = user.hasPrivilege(privilege)
    if (hasPrivilege) {
      return !this.isPrivilegeRestricted(privilege)
    }
    return false
  }

  hasAdditionalPrivilege(privilege: Privilege) {
    return this.userAdditionalPrivileges?.indexOf(privilege) != -1
  }

  isStateApproved() {
    return this.approved && (this.status == 'AGENCY_APPROVED' || this.status == 'CLIENT_APPROVED')
  }

  isOfSow = () => this.scopeOfWorkVersion != null
  isArchived = () => this.archived
  isTemplate = () => this.identity.isTemplate
  isDraftOrConfigDraft = () => [StatusType.DRAFT, StatusType.CONFIG_DRAFT].includes(this.status)

  hasEnoughBudget() {
    if (this.budget) {
      if (this.getTotalSellingPrice() != null) {
        return (this.budget.amount || 0) > (this.getTotalSellingPrice().amount || 0)
      } else {
        return true
      }
    }
    return false
  }

  removeSection(section: ScopeSection) {
    this.sections = this.sections.filter((s) => s.id != section.id)
  }

  removeDeliverable(deliverable: Deliverable) {
    this.deliverables = this.deliverables.filter((s) => s.id != deliverable.id)
  }

  findDeliverableById(id: number): Deliverable {
    return this.deliverables.find((d) => d.id == id)
  }

  totalPercentageComplete(excludeDeliverableId?: number): number {
    if (this.calculatedValues) return this.calculatedValues.totalPercentageComplete

    let totalHours = 0
    let totalPercentage = 0

    this.deliverables
      .filter(isNonTradedDeliverable)
      .filter((d) => excludeDeliverableId == null || excludeDeliverableId !== d.id)
      .forEach((d) => {
        totalHours += d.traffickedActualMinutes || 0
        totalPercentage += (d.traffickedPercentageComplete || 0) * (d.traffickedActualMinutes || 0)
      })

    return totalHours === 0 ? 0 : totalPercentage / totalHours
  }

  getAllComponents() {
    return this.deliverables.flatMap((d) => d.components)
  }

  totalActualHours(excludeDeliverableId?: number): number {
    if (this.calculatedValues) return this.calculatedValues.totalActualMinutes / 60

    let total = 0

    this.deliverables
      .filter(isNonTradedDeliverable)
      .filter((d) => excludeDeliverableId == null || excludeDeliverableId !== d.id)
      .forEach((d) => {
        total += d.traffickedActualMinutes || 0
      })

    return total / 60
  }

  getMaximumStartDate() {
    return this.endDate
  }

  getMinimumEndDate() {
    return this.startDate
  }

  getScopeByRoleTotalMargin = () => this.getMarginPercentage(true)

  findCollaboratorByUserId(userId: number) {
    return this.collaborators.find((collaborator) => collaborator.user.id == userId)
  }

  getDeliverablesWithoutSection = (): Deliverable[] => {
    return this.deliverables?.filter((d) => d.section == null)
  }

  getMarginPercentage(format = false): string {
    const margin = this.calculatedValues
      ? this.margin || 0
      : (this.getTotalProfit().amount / (this.getTotalSellingPrice() ? this.getTotalSellingPrice().amount : 1)) * 100

    return format ? margin.toFixed(2) + '%' : margin.toFixed(2)
  }

  private getMoneyWithScopeCurrency(amount: number): Money {
    return new Money(amount, this.currency)
  }
}

export enum StatusType {
  DRAFT = 'DRAFT',
  SUBMITTED = 'SUBMITTED',
  IN_REVIEW = 'IN_REVIEW',
  REVIEWED = 'REVIEWED',
  AGENCY_APPROVED = 'AGENCY_APPROVED',
  CLIENT_APPROVED = 'CLIENT_APPROVED',
  TRAFFICKED = 'TRAFFICKED',
  CLOSED = 'CLOSED',
  SCOPING = 'SCOPING',
  CONFIG_DRAFT = 'CONFIG_DRAFT',
}

export type ScopeStatus =
  | 'DRAFT'
  | 'SUBMITTED'
  | 'REVIEWED'
  | 'AGENCY_APPROVED'
  | 'CLIENT_APPROVED'
  | 'CLOSED'
  | 'CONFIG_DRAFT'

export type ScopeStatusName = Record<ScopeStatus, string>
export const scopeStatuses: ScopeStatusName = {
  DRAFT: 'DRAFT',
  SUBMITTED: 'SUBMITTED',
  REVIEWED: 'REVIEWED',
  AGENCY_APPROVED: 'AGENCY APPROVED',
  CLIENT_APPROVED: 'CLIENT APPROVED',
  CLOSED: 'CLOSED',
  CONFIG_DRAFT: 'CONFIG DRAFT',
}

type ScopeApprovedWebHookInfo = Record<string, any>

export enum DefaultView {
  table = 'table',
  timeline = 'timeline',
}

export enum ScopeTab {
  TABLE = 'Table',
  TIMELINE = 'Timeline',
}

export type ScopeConfiguration = Record<number, ScopeConfigurationCategory>

export type ScopeConfigurationCategory = Record<number, string>
